import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from './firebase';
import { imageProcessingService } from './image-processing-service';
import { analyticsService } from './analytics-service';
import { AdSpot } from '@/types/ad-management';

export class AdManagementService {
  private static instance: AdManagementService;
  private readonly COLLECTION_NAME = 'ad_spots';
  private readonly STORAGE_PATH = 'ad-images';
  
  // Impression deduplication - track recent impressions to prevent spam
  private recentImpressions = new Map<string, number>();
  private readonly IMPRESSION_COOLDOWN = 60000; // 1 minute cooldown per ad

  public static getInstance(): AdManagementService {
    if (!AdManagementService.instance) {
      AdManagementService.instance = new AdManagementService();
    }
    return AdManagementService.instance;
  }

  private getDb() {
    if (!db) {
      throw new Error('Database not initialized');
    }
    return db;
  }

  private getStorage() {
    if (!storage) {
      throw new Error('Storage not initialized');
    }
    return storage;
  }

  // Get all ad spots
  async getAdSpots(): Promise<AdSpot[]> {
    try {
      const database = this.getDb();
      const querySnapshot = await getDocs(
        query(
          collection(database, this.COLLECTION_NAME),
          orderBy('priority', 'desc'),
          orderBy('createdAt', 'desc')
        )
      );
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as AdSpot));
    } catch (error) {
      throw new Error('Failed to fetch ad spots');
    }
  }

  // Get ad spots by location
  async getAdSpotsByLocation(location: string): Promise<AdSpot[]> {
    try {
      const database = this.getDb();
      const querySnapshot = await getDocs(
        query(
          collection(database, this.COLLECTION_NAME),
          where('location', '==', location),
          where('enabled', '==', true),
          orderBy('priority', 'desc')
        )
      );
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as AdSpot));
    } catch (error) {
      return [];
    }
  }

  // Get single ad spot
  async getAdSpot(id: string): Promise<AdSpot | null> {
    try {
      const database = this.getDb();
      const docSnap = await getDoc(doc(database, this.COLLECTION_NAME, id));
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as AdSpot;
      }
      
      return null;
    } catch (error) {
      throw new Error('Failed to fetch ad spot');
    }
  }

  // Create new ad spot
  async createAdSpot(adSpot: Omit<AdSpot, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const database = this.getDb();
      const now = new Date().toISOString();
      const newAdSpot: Omit<AdSpot, 'id'> = {
        ...adSpot,
        createdAt: now,
        updatedAt: now
      };

      const docRef = doc(collection(database, this.COLLECTION_NAME));
      await setDoc(docRef, newAdSpot);
      
      return docRef.id;
    } catch (error) {
      throw new Error('Failed to create ad spot');
    }
  }

  // Update ad spot
  async updateAdSpot(id: string, updates: Partial<AdSpot>): Promise<void> {
    try {
      const database = this.getDb();
      const docRef = doc(database, this.COLLECTION_NAME, id);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      throw new Error('Failed to update ad spot');
    }
  }

  // Delete ad spot
  async deleteAdSpot(id: string): Promise<void> {
    try {
      const database = this.getDb();
      // First get the ad spot to check if it has an image
      const adSpot = await this.getAdSpot(id);
      
      // Delete the document
      await deleteDoc(doc(database, this.COLLECTION_NAME, id));
      
      // Delete associated image if it exists
      if (adSpot?.imageUrl) {
        await this.deleteAdImage(adSpot.imageUrl);
      }
    } catch (error) {
      throw new Error('Failed to delete ad spot');
    }
  }

  // Upload ad image with WebP conversion
  async uploadAdImage(file: File, adSpotId: string): Promise<string> {
    try {
      const firebaseStorage = this.getStorage();
      // Process image - optimize while preserving GIF animation for ads
      const processedImage = await imageProcessingService.processForAdImage(file);
      
      // Generate filename with appropriate extension
      const optimizedFileName = processedImage.format === 'gif' 
        ? imageProcessingService.generateOptimizedFilename(`${adSpotId}-${file.name}`, 'image/gif')
        : imageProcessingService.generateWebPFilename(`${adSpotId}-${file.name}`);
      const storageRef = ref(firebaseStorage, `${this.STORAGE_PATH}/${optimizedFileName}`);
      
      // Upload processed image
      const snapshot = await uploadBytes(storageRef, processedImage.blob);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Log optimization results
      const savings = imageProcessingService.calculateStorageSavings(
        processedImage.originalSize, 
        processedImage.compressedSize
      );
      
      if (processedImage.format === 'gif') {
        console.log('GIF image processed, skipping compression');
      } else {
        console.log('Image compressed successfully');
      }
      
      return downloadURL;
    } catch (error) {
      throw new Error('Failed to upload ad image');
    }
  }

  // Delete ad image
  async deleteAdImage(imageUrl: string): Promise<void> {
    try {
      const firebaseStorage = this.getStorage();
      // Extract the file path from the URL
      const urlParts = imageUrl.split('/');
      const fileToken = urlParts[urlParts.length - 1];
      const fileName = fileToken.split('?')[0];
      
      const storageRef = ref(firebaseStorage, `${this.STORAGE_PATH}/${fileName}`);
      await deleteObject(storageRef);
    } catch (error) {
      // Don't throw here as the main operation (deleting ad spot) should still succeed
    }
  }

  // Track ad impression with deduplication and enhanced analytics
  async trackImpression(adSpotId: string, context?: { location?: string; userAgent?: string; referrer?: string }): Promise<void> {
    try {
      // Skip tracking during build/SSR phase
      if (typeof window === 'undefined') {
        return;
      }
      
      const now = Date.now();
      const lastTracked = this.recentImpressions.get(adSpotId);
      
      // Check if we've tracked this ad recently (within cooldown period)
      if (lastTracked && (now - lastTracked) < this.IMPRESSION_COOLDOWN) {
        return; // Skip tracking to prevent spam
      }
      
      // Update the tracking timestamp
      this.recentImpressions.set(adSpotId, now);
      
      // Clean up old entries (older than 2x cooldown period)
      const cutoffTime = now - (this.IMPRESSION_COOLDOWN * 2);
      for (const [id, timestamp] of this.recentImpressions.entries()) {
        if (timestamp < cutoffTime) {
          this.recentImpressions.delete(id);
        }
      }
      
      const database = this.getDb();
      if (!database) {
        return; // No database connection available
      }
      
      const docRef = doc(database, this.COLLECTION_NAME, adSpotId);
      const adSpot = await this.getAdSpot(adSpotId);
      
      if (adSpot) {
        // Update basic impression count
        await updateDoc(docRef, {
          impressions: (adSpot.impressions || 0) + 1,
          updatedAt: new Date().toISOString()
        });

        // Track detailed analytics (Phase 1)
        await analyticsService.trackAnalyticsEvent({
          adSpotId,
          location: context?.location || adSpot.location,
          eventType: 'impression',
          userAgent: context?.userAgent,
          referrer: context?.referrer
        });
      }
    } catch (error) {
      // Don't throw here as this is analytics tracking
    }
  }

  // Track ad click with enhanced analytics
  async trackClick(adSpotId: string, context?: { location?: string; userAgent?: string; referrer?: string }): Promise<void> {
    try {
      // Skip tracking during build/SSR phase
      if (typeof window === 'undefined') {
        return;
      }
      
      const database = this.getDb();
      if (!database) {
        return; // No database connection available
      }
      
      const docRef = doc(database, this.COLLECTION_NAME, adSpotId);
      const adSpot = await this.getAdSpot(adSpotId);
      
      if (adSpot) {
        // Update basic click count
        await updateDoc(docRef, {
          clicks: (adSpot.clicks || 0) + 1,
          updatedAt: new Date().toISOString()
        });

        // Track detailed analytics (Phase 1)
        await analyticsService.trackAnalyticsEvent({
          adSpotId,
          location: context?.location || adSpot.location,
          eventType: 'click',
          userAgent: context?.userAgent,
          referrer: context?.referrer
        });
      }
    } catch (error) {
      // Don't throw here as this is analytics tracking
    }
  }

  // Get active ads for a specific location (filtered by date and enabled status)
  async getActiveAdsForLocation(location: string): Promise<AdSpot[]> {
    try {
      const now = new Date().toISOString();
      const allAds = await this.getAdSpotsByLocation(location);
      
      return allAds.filter(ad => {
        // Check if ad is enabled
        if (!ad.enabled) return false;
        
        // Check start date
        if (ad.startDate && ad.startDate > now) return false;
        
        // Check end date
        if (ad.endDate && ad.endDate < now) return false;
        
        return true;
      });
    } catch (error) {
      return [];
    }
  }
}

export const adManagementService = AdManagementService.getInstance();

